import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Rocket, Undo2, Boxes, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/brand/Pill";
import { Card, CardHead } from "@/components/brand/Card";
import {
  useReferenceDataVersions,
  useReferenceDataHistory,
  usePromoteReferenceData,
  useReferenceDataSources,
  useReferenceDataAgents,
  useConfigPackages,
} from "@/hooks/useApi";
import type { ReferenceDataVersion } from "@/types/reference-data";
import { formatRelative } from "@/lib/datetime";
import { SSCUI_LIST_DATASET } from "@/api/reference-data";
import { configTypePathFor } from "@/lib/reference-data-config-types";
import { errMessage } from "@/lib/reference-data-edit";
import {
  AUDIT_PREVIEW,
  AuditRow,
  Loading,
  Empty,
  ErrorBlock,
  StaleNotice,
} from "@/components/admin/reference-data/shared";
import { promoteMessage } from "@/lib/reference-data-download";
import { ConfirmDialog, RollbackDialog } from "@/components/admin/reference-data/dialogs";

// ---------------------------------------------------------------------------
// Versions & audit tab
// ---------------------------------------------------------------------------

/** What activating a given version means, relative to the current state.
 *
 *  Fonte avulsa: a tabela é versionada por chave composta, então várias runs
 *  podem estar ativas ao mesmo tempo para chaves diferentes — `is_active` NÃO é
 *  exclusivo. O "head" é a run mais nova que ainda tem linha ativa (o estado em
 *  que se está). O que é mais novo que o head é rascunho ainda não promovido
 *  (promote); o que é mais antigo é versão anterior que se pode restaurar
 *  (rollback), mesmo que ainda guarde algumas chaves ativas.
 *
 *  Tabela de tipo: a posição na lista não diz nada, porque as edições da tabela e
 *  os pacotes se intercalam e o head de edições pode nem existir. Cada versão se
 *  decide sozinha. A de pacote, pelo estado do pacote na lista de releases do tipo
 *  (ativo, staged e demais). A de edição, pelo `promoted_at` (sem ele é sempre
 *  rascunho) e, se promovida, pelo pacote ativo listado: a edição promovida em cima
 *  do pacote ativo fica sem selo porque o selo já é do pacote. "unknown" é o que
 *  ainda não dá para afirmar: fontes, resumo do tipo ou lista de pacotes ainda sem
 *  dado. Em tabela de tipo não há rollback nem promote de pacote aqui: o botão
 *  leva às releases do tipo (spec 2026-10-21, D9). */
type VersionAction = "active" | "promote" | "rollback" | "unknown";

export function VersionsTab({
  tableName,
  onOpenPackageReleases,
}: {
  tableName: string;
  /** Na tela do tipo, troca o link das releases por trocar de aba. */
  onOpenPackageReleases?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: sourcesData,
    isError: sourcesError,
    refetch: refetchSources,
  } = useReferenceDataSources();
  const configType =
    sourcesData?.sources.find((s) => s.table_name === tableName)?.config_type ?? null;
  // O resumo dos agentes só interessa a tabela de tipo; fonte avulsa não o pede.
  const { data: agentsData, isError: agentsError } = useReferenceDataAgents({
    enabled: Boolean(configType),
  });
  // O estado de cada pacote (ativo, staged, superseded…) decide a ação da versão de pacote.
  const {
    data: packagesData,
    isError: packagesError,
    refetch: refetchPackages,
  } = useConfigPackages(
    configType ?? undefined,
  );
  const packageReleasesPath = configTypePathFor(agentsData?.agents, configType, "releases");
  const openPackageReleases =
    onOpenPackageReleases ?? (packageReleasesPath ? () => navigate(packageReleasesPath) : undefined);
  const isPackage = (v: ReferenceDataVersion) =>
    Boolean(v.package_id) || v.dataset === SSCUI_LIST_DATASET;
  // Release legada (antes do conceito de pacote) não tem package_id; o id de
  // pacote dela é o da própria run.
  const packageIdOf = (v: ReferenceDataVersion): string | null =>
    v.package_id ?? (v.dataset === SSCUI_LIST_DATASET ? v.ingest_run_id : null);
  const typeSummary = agentsData?.agents
    .flatMap((a) => a.config_types)
    .find((c) => c.key === configType);
  const summaryActivePackageId = typeSummary?.active_release?.package_id ?? null;
  const packageStateOf = (id: string) => packagesData?.items.find((p) => p.package_id === id)?.state;
  // Uma fonte só: a lista de pacotes manda (resumo e lista atualizam em ritmos diferentes).
  // O resumo vale apenas para a release legada que não aparece na lista.
  const isActivePackage = (id: string) => {
    const listed = packageStateOf(id);
    return listed ? listed === "active" : id === summaryActivePackageId;
  };
  // Sem as fontes não dá para saber se a tabela é de tipo: nada de Rollback ainda.
  const sourcesKnown = Boolean(sourcesData);
  // Em tabela de tipo o selo depende do resumo e da lista de pacotes juntos.
  const typeKnown = Boolean(typeSummary) && Boolean(packagesData);
  const {
    data: versions,
    isLoading: versionsLoading,
    isError: versionsError,
    refetch: refetchVersions,
  } = useReferenceDataVersions(tableName);
  const {
    data: history,
    isLoading: historyLoading,
    isError: historyError,
    refetch: refetchHistory,
  } = useReferenceDataHistory(tableName, { limit: 50 });
  const promoteMutation = usePromoteReferenceData(tableName);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<ReferenceDataVersion | null>(null);
  const [auditExpanded, setAuditExpanded] = useState(false);

  // Erro sem nenhum dado vira bloco com retry (nunca "vazio"); com dado em cache vira aviso.
  const sourcesFailed = sourcesError && !sourcesData;
  const versionsFailed = versionsError && !versions;
  const historyFailed = historyError && !history;
  const versionsStale =
    (versionsError && Boolean(versions)) || (sourcesError && Boolean(sourcesData));

  const auditItems = history?.items ?? [];
  const shownAudit = auditExpanded ? auditItems : auditItems.slice(0, AUDIT_PREVIEW);

  const items = versions?.items ?? [];
  // Fonte avulsa: head posicional, a run mais nova (ordem DESC) que ainda tem linhas ativas.
  const headIdx = items.findIndex((v) => v.active_row_count > 0);
  const activePackageListed = items.some((v) => {
    const id = packageIdOf(v);
    return id !== null && isActivePackage(id);
  });
  // Edição (sem pacote) promovida e com linhas ativas mais nova da lista: leva o selo.
  const activeEditIdx = items.findIndex(
    (v) => !packageIdOf(v) && Boolean(v.promoted_at) && v.active_row_count > 0,
  );
  const typeTableAction = (index: number): VersionAction => {
    const version = items[index];
    const packageId = packageIdOf(version);
    if (packageId) {
      if (!typeKnown) return "unknown";
      if (isActivePackage(packageId)) return "active";
      const packageState = packageStateOf(packageId);
      if (packageState === "staged") return "promote";
      if (packageState) return "rollback";
      // Fora da lista de releases (legada): vale o que a própria run diz.
      return version.promoted_at ? "rollback" : "promote";
    }
    // Edição sem promoted_at é sempre rascunho, em qualquer posição.
    if (!version.promoted_at) return "promote";
    if (version.active_row_count === 0) return "rollback";
    if (!typeKnown || activePackageListed) return "unknown";
    return index === activeEditIdx ? "active" : "rollback";
  };
  const actionFor = (index: number): VersionAction => {
    // Sem as fontes não dá para saber se a tabela é de tipo: nenhuma versão ganha selo nem ação.
    if (!sourcesKnown) return "unknown";
    if (configType) return typeTableAction(index);
    if (index === headIdx) return "active";
    if (headIdx === -1) return index === 0 ? "promote" : "rollback";
    return index < headIdx ? "promote" : "rollback";
  };

  // Rollback targets = every version older than the head (they were live
  // before). Staged drafts newer than the head are promoted, not rolled back.
  const rollbackCandidates = items.filter((_v, i) => actionFor(i) === "rollback");
  // A promotable draft exists = there's a newer, not-yet-active version. Used
  // to explain, inside the dialog, why there may be nothing to roll back to.
  const hasDraft = items.some((_v, i) => actionFor(i) === "promote");

  const confirmPromote = async () => {
    if (!promoteTarget) return;
    try {
      const res = await promoteMutation.mutateAsync(promoteTarget.ingest_run_id);
      toast.success(promoteMessage(t, res));
    } catch (err) {
      toast.error(errMessage(err, t("Promotion failed.")));
    } finally {
      setPromoteTarget(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <Card>
        <CardHead
          overline={t("IMMUTABLE VERSIONS")}
          title={t("Version history")}
          right={
            configType ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={openPackageReleases}
                disabled={!openPackageReleases}
                title={
                  openPackageReleases ? undefined : t("Releases unavailable until the type data loads.")
                }
              >
                <Boxes className="w-3.5 h-3.5" /> {t("Package releases")}
              </Button>
            ) : sourcesKnown ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setRollbackOpen(true)}
                disabled={items.length <= 1}
              >
                <Undo2 className="w-3.5 h-3.5" /> {t("Rollback")}
              </Button>
            ) : undefined
          }
        />
        <div className="p-2">
          {versionsStale && <StaleNotice />}
          {configType && packagesError && (
            packagesData ? (
              <StaleNotice />
            ) : (
              <StaleNotice
                message={t("Could not load the state of the packages.")}
                onRetry={() => void refetchPackages()}
              />
            )
          )}
          {configType && agentsError && (
            agentsData ? (
              <StaleNotice />
            ) : (
              <StaleNotice
                message={t("Could not load the active version badge; it will appear once the data loads.")}
              />
            )
          )}
          {versionsLoading ? (
            <Loading />
          ) : sourcesFailed || versionsFailed ? (
            <ErrorBlock
              message={t("Could not load the versions.")}
              onRetry={() => {
                if (sourcesFailed) void refetchSources();
                if (versionsFailed) void refetchVersions();
              }}
            />
          ) : items.length === 0 ? (
            <Empty text={t("No versions yet.")} />
          ) : (
            <div className="divide-y divide-[#f2f4f7]">
              {items.map((v, index) => {
                const action = actionFor(index);
                return (
                  <div key={v.ingest_run_id} className="flex items-center gap-3 px-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="mono text-[12px] text-[#08254c]">
                          {v.versions.join(", ") || t("(unversioned)")}
                        </span>
                        {action === "active" && <Pill tone="ok">{t("Active")}</Pill>}
                        {action === "promote" && <Pill tone="gold">{t("Draft")}</Pill>}
                        {action === "rollback" && (
                          <span className="mono muted text-[10px] uppercase tracking-[0.06em]">
                            {t("previous")}
                          </span>
                        )}
                        {isPackage(v) && (
                          <Pill tone="navy">
                            {t("Package")} · {v.sap_release ?? "—"}
                          </Pill>
                        )}
                      </div>
                      <div className="mono muted text-[11px] mt-0.5">
                        {formatRelative(v.created_at)} · {v.row_count.toLocaleString()} {t("rows")} ·{" "}
                        {v.active_row_count.toLocaleString()} {t("active")}
                      </div>
                    </div>
                    {action === "promote" && isPackage(v) && configType && openPackageReleases && (
                      <button
                        type="button"
                        className="mono muted text-[10px] underline underline-offset-2 hover:text-[#08254c]"
                        onClick={openPackageReleases}
                      >
                        {t("Promote in the package releases")}
                      </button>
                    )}
                    {action === "promote" && !isPackage(v) && (
                      <Button
                        variant="action"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setPromoteTarget(v)}
                        disabled={promoteMutation.isPending}
                      >
                        <Rocket className="w-3.5 h-3.5" /> {t("Promote")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {sourcesKnown && !configType && (
        <RollbackDialog
          tableName={tableName}
          open={rollbackOpen}
          onOpenChange={setRollbackOpen}
          candidates={rollbackCandidates}
          hasDraft={hasDraft}
        />
      )}

      <ConfirmDialog
        open={!!promoteTarget}
        onOpenChange={(o) => !o && setPromoteTarget(null)}
        title={t("Promote version")}
        description={t(
          "Promote this version to active? The agents will read it immediately. In-flight runs stay pinned to the current one. Requires a passed sanity check.",
        )}
        confirmLabel={t("Promote")}
        confirmIcon={<Rocket className="w-3.5 h-3.5" />}
        onConfirm={confirmPromote}
        loading={promoteMutation.isPending}
      />

      <Card>
        <CardHead overline={t("EVERY RUN AUDITED")} title={t("Audit log")} />
        <div className="p-2">
          {historyError && Boolean(history) && <StaleNotice />}
          {historyLoading ? (
            <Loading />
          ) : historyFailed ? (
            <ErrorBlock
              message={t("Could not load the audit log.")}
              onRetry={() => void refetchHistory()}
            />
          ) : auditItems.length === 0 ? (
            <Empty text={t("No runs recorded.")} />
          ) : (
            <>
              <div className="divide-y divide-[#f2f4f7]">
                {shownAudit.map((r) => (
                  <AuditRow key={r.id} run={r} />
                ))}
              </div>
              {auditItems.length > AUDIT_PREVIEW && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAuditExpanded((e) => !e)}
                  className="w-full mt-1 gap-1.5 py-2.5 font-mono text-[11px] uppercase tracking-[0.06em]"
                >
                  {auditExpanded ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5" /> {t("Show less")}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      {t("Show all ({{count}})", { count: auditItems.length })}
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
